'use client';
import React from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { FilterBar as SharedFilterBar } from '@launchstore/shared-puck';
import type { FilterBarProps, SortKey } from '@launchstore/shared-puck';
import { usePuckContext } from '@/components/puck/PuckContextProvider';

/**
 * Storefront FilterBar wrapper — wires the shared presentational component
 * to real URL-driven state + reads totalCount/filterGroups from Puck context.
 */
const SharedRender = (SharedFilterBar as any).render;
const SharedGrid: React.FC<any> = (props) => <SharedRender {...props} />;

function FilterBarImpl(rawProps: any) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { context } = usePuckContext() as any;

  // Read current state from URL
  const currentSort = (searchParams.get('sortBy') || 'created_desc') as SortKey;

  // Build active filters map from URL (any param that isn't sortBy/page/q)
  const activeFilters: Record<string, string[]> = {};
  const reservedKeys = ['sortBy', 'page', 'q'];
  searchParams.forEach((value, key) => {
    if (!reservedKeys.includes(key)) {
      if (!activeFilters[key]) activeFilters[key] = [];
      activeFilters[key].push(value);
    }
  });

  // Read totalCount + filterGroups from Puck context (injected by the page)
  const totalCount = context?.totalCount;
  const filterGroups = context?.filterGroups;

  // Callbacks — update URL query params
  const handleSortChange = (sort: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('sortBy', sort);
    params.delete('page');
    router.push(`${pathname}?${params.toString()}`);
  };

  const handleFilterChange = (group: string, value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    const existing = params.getAll(group);
    if (existing.includes(value)) {
      params.delete(group);
      existing.filter((v) => v !== value).forEach((v) => params.append(group, v));
    } else {
      params.append(group, value);
    }
    params.delete('page');
    router.push(`${pathname}?${params.toString()}`);
  };

  const handleClearAll = () => {
    // Clear *filters* only — the search query (q) survives, matching the
    // legacy RefinementList and standard marketplace behaviour (clearing a
    // size/colour filter must never erase the user's search).
    const params = new URLSearchParams();
    const sort = searchParams.get('sortBy');
    if (sort) params.set('sortBy', sort);
    const q = searchParams.get('q');
    if (q) params.set('q', q);
    router.push(`${pathname}?${params.toString()}`);
  };

  return (
    <SharedGrid
      {...rawProps}
      currentSort={currentSort}
      activeFilters={activeFilters}
      totalCount={totalCount}
      filterGroups={filterGroups}
      showingCount={typeof totalCount === 'number' ? Math.min(totalCount, 12) : undefined}
      onSortChange={handleSortChange}
      onFilterChange={handleFilterChange}
      onClearAll={handleClearAll}
    />
  );
}

export const FilterBar: any = {
  ...SharedFilterBar,
  render: (props: any) => <FilterBarImpl {...props} />,
};
export default FilterBar;
